"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Form,
  FormField,
  FormItem,
  FormLabel,
  ScrollArea,
} from "@reactive-resume/ui";
import { useDialog } from "@/client/stores/dialog";
import { useScore } from "@/client/services/ollama/ats-score";
import { useResumeStore } from "@/client/stores/resume";
import { useForm } from "react-hook-form";
import {
  XIcon,
  ShieldCheckIcon,
  FileTextIcon,
  ListDashesIcon,
  CheckCircleIcon,
  SparkleIcon,
  RobotIcon,
  DownloadSimpleIcon,
  ArrowClockwiseIcon,
  ClockIcon,
  StarFourIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useLocaleStorage } from "@/client/hooks/use-locale-storage";
type FormValue = { jobdesc: string };
import { cn } from "@reactive-resume/utils";

// --- Composant Jauge ---
const CircularDonut = ({
  value,
  max = 100,
  colorClass,
  size = 120,
  strokeWidth = 8,
  showText = true,
  showRing = true,
  label,
  isBig,
}: {
  value?: number;
  max?: number;
  colorClass: string;
  size?: number;
  strokeWidth?: number;
  label?: string;
  showText?: boolean;
  showRing?: boolean;
  isBig?: boolean;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = value !== undefined ? Math.min((value / max) * 100, 100) : 0;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle
          className="text-border/30"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {showRing && value !== undefined && (
          <circle
            className={`${colorClass} transition-all duration-700 ease-out`}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
        )}
      </svg>

      {showText && value !== undefined && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center leading-tight">
          <span className={cn("&& text-lg font-bold text-text", colorClass, isBig && "text-4xl")}>
            {value}
          </span>
          {/* <span className={cn("text-xs text-text-muted", colorClass)}>/ {max}</span> */}
        </div>
      )}
      {!showText && !showRing && label === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <ShieldCheckIcon className="size-12 text-accent-primary opacity-50" weight="light" />
        </div>
      )}
    </div>
  );
};

// --- Helper pour récupérer les classes de thème et le statut ---
const getStatusData = (score: number) => {
  if (score >= 80) {
    return {
      text: "text-status-success",
      bg: "bg-status-success-subtle",
      border: "border-status-success-border",
      label: "Excellent",
    };
  } else if (score >= 60) {
    return {
      text: "text-status-info",
      bg: "bg-status-info-subtle",
      border: "border-status-info-border",
      label: "Bon",
    };
  } else {
    return {
      text: "text-status-danger",
      bg: "bg-status-danger-subtle",
      border: "border-status-danger-border",
      label: "À améliorer",
    };
  }
};

// --- Composant Principal ---
export const DialogScoring = () => {
  const { isOpen, close } = useDialog("scoring");
  const resume = useResumeStore((s) => s.resume);
  const [value, setValue] = useLocaleStorage(resume.id, "");
  const {
    ats_score,
    skills_match_percentage,
    experience_relevance,
    missing_keywords,
    improvement_suggestions,
  } = value;
  const score = useScore(resume.id);
  const formAts = useForm<FormValue>({ defaultValues: { jobdesc: "" } });
  const onSubmit = async (data: FormValue) => {
    await score.mutateAsync({ resume: resume.data, jobdesc: data.jobdesc });
  };
  const [showLastResult, setShowLastResult] = useState(value ? true : false);

  useEffect(() => {
    if (score.data) {
      setValue(score.data);
    }
  }, [score, value]);

  useEffect(() => {
    if (value) setShowLastResult(true);
  }, [value]);

  // Regroupement des suggestions par catégories
  const groupedSuggestions: Record<"skills" | "keywords" | "experience" | "formatting", string[]> =
    {
      skills: [],
      keywords: [],
      experience: [],
      formatting: [],
    };

  (improvement_suggestions || []).forEach((item: { category?: string; suggestion?: string }) => {
    const category = item.category as
      | "skills"
      | "keywords"
      | "experience"
      | "formatting"
      | undefined;

    if (category && category in groupedSuggestions) {
      groupedSuggestions[category].push(item.suggestion ?? "");
    }
  });

  // Construction des 3 cartes dynamiques
  const cards = [
    {
      id: "content",
      title: "Compétences & Mots-clés",
      icon: FileTextIcon,
      score: skills_match_percentage || 0,
      points: [...(groupedSuggestions.skills || []), ...(groupedSuggestions.keywords || [])],
      missingKeywords: missing_keywords || [],
    },
    {
      id: "experience",
      title: "Expérience",
      icon: ListDashesIcon,
      score: experience_relevance || 0,
      points: groupedSuggestions.experience || [],
      missingKeywords: [],
    },
    {
      id: "format",
      title: "Format & Structure",
      icon: CheckCircleIcon,
      score: ats_score || 0,
      points: groupedSuggestions.formatting || [],
      missingKeywords: [],
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="min-w-[1000px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border-subtle p-6 pb-2">
          <div className="flex items-start gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-amber-100/70 text-amber-600">
              <ShieldCheckIcon className="size-6" weight="bold" />
            </div>
            <div>
              <DialogTitle className="mb-1 text-lg">Score ATS</DialogTitle>
              <div className="text-sm text-text-muted">
                {score.isPending
                  ? "Analyse de votre CV en cours... Merci de patienter."
                  : score.data
                    ? "Analyse terminée ! Voici la compatibilité de votre CV avec les systèmes ATS."
                    : "Vérifiez la compatibilité de votre CV avec les systèmes ATS."}
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] p-6">
          {/* 1. ETAT : CHARGEMENT */}
          {score.isPending && (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div className="flex flex-col items-center space-y-6 pt-4 text-center">
                <div className="size-24 animate-spin rounded-full border-4 border-accent-primary border-b-transparent border-t-transparent"></div>{" "}
                {/* <CircularDonut colorClass="text-action-primary" size={160} strokeWidth={6} showText={false} label="loading" /> */}
                <div>
                  <h3 className="text-lg font-semibold text-accent-primary">Analyse en cours...</h3>
                  <p className="mt-1 max-w-[250px] text-sm text-text-secondary">
                    Nous analysons votre CV par rapport aux critères des systèmes ATS.
                  </p>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-status-info-border bg-status-info-subtle/40 px-4 py-2">
                  <ClockIcon className="size-4 text-icon-info" />
                  <span className="text-xs text-text-secondary">
                    Cela peut prendre quelques secondes.
                  </span>
                </div>
                {/* <div className="bg-accent-primary-subtle p-5 rounded-xl text-left w-full border border-accent-primary-border/40">
                  <div className="flex items-start gap-3">
                    <StarFourIcon className="size-5 text-icon-brand shrink-0 mt-0.5" weight="bold" />
                    <div>
                      <div className="text-sm font-semibold text-text">Bon à savoir</div>
                      <div className="text-xs text-text-secondary leading-relaxed mt-1">Nous vérifions plus de 20 critères ATS pour vous fournir une analyse complète.</div>
                    </div>
                  </div>
                </div> */}
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-text">Analyse en cours</h4>
                  <p className="text-xs text-text-muted">
                    Notre IA évalue votre CV sur 3 aspects clés.
                  </p>
                </div>
                {[
                  {
                    title: "Compétences & Mots-clés",
                    desc: "Analyse des compétences et adéquation des mots-clés...",
                    color: "text-rose-500",
                    icon: FileTextIcon,
                  },
                  {
                    title: "Expérience",
                    desc: "Vérification de la pertinence de l'expérience...",
                    color: "text-blue-500",
                    icon: ListDashesIcon,
                  },
                  {
                    title: "Format & Structure",
                    desc: "Évaluation de la mise en page et lisibilité...",
                    color: "text-green-500",
                    icon: CheckCircleIcon,
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="relative flex items-center justify-between overflow-hidden rounded-xl border border-border bg-surface p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`size-10 rounded-full bg-${item.color.replace("text-", "")}-100/30 flex items-center justify-center`}
                      >
                        <item.icon className={`size-5 ${item.color}`} />
                      </div>
                      <div>
                        <div className={`text-sm font-semibold ${item.color}`}>{item.title}</div>
                        <div className="max-w-[180px] text-xs text-text-muted">{item.desc}</div>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(6)].map((_, idx) => (
                        <div
                          key={idx}
                          className={`size-1.5 rounded-full ${item.color} animate-pulse`}
                          style={{ animationDelay: `${idx * 0.15}s` }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                <div className="mt-4 flex items-center justify-between rounded-xl border border-accent-primary-border/40 bg-accent-primary-subtle p-4">
                  <div className="flex items-center gap-3">
                    <SparkleIcon className="size-6 text-icon-brand" weight="duotone" />
                    <div>
                      <div className="text-sm font-medium text-text">
                        Ne fermez pas cette fenêtre pendant l'analyse
                      </div>
                      <div className="text-xs text-text-secondary">
                        Nous vous préviendrons dès que les résultats seront prêts.
                      </div>
                    </div>
                  </div>
                  <div className="flex size-10 items-center justify-center rounded-full border border-border-subtle bg-surface">
                    <RobotIcon className="size-6 text-text-muted" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 2. ETAT : RÉSULTATS TERMINÉS */}
          {!score.isPending && value && showLastResult && (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              {/* Panneau Gauche : Score Global */}
              <div className="flex flex-col items-center space-y-5 pt-4 text-center lg:col-span-4">
                <CircularDonut
                  value={ats_score}
                  colorClass="text-action-primary"
                  size={160}
                  strokeWidth={10}
                  isBig={true}
                />
                <div>
                  <h3 className="text-xl font-semibold text-action-primary">
                    {ats_score >= 70 ? "Bon score ATS" : "Score à améliorer"}
                  </h3>
                  <p className="mt-1 px-4 text-sm text-text-secondary">
                    {ats_score >= 70
                      ? "Votre CV a de bonnes chances de passer les filtres ATS."
                      : "Appliquez nos recommandations pour augmenter vos chances."}
                  </p>
                </div>

                {/* Échelle des scores */}
                <div className="w-full rounded-xl border border-border-subtle bg-surface-hover p-4 text-left">
                  <div className="mb-2 text-xs font-medium text-text-muted">
                    Ce score signifie :
                  </div>
                  <ul className="space-y-1.5 text-xs text-text-secondary">
                    <li className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-status-success" /> 80 - 100 :{" "}
                      <span className="text-text">Excellent</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-status-info" /> 60 - 79 :{" "}
                      <span className="text-text">Bon</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="size-2 rounded-full bg-status-danger" /> 0 - 59 :{" "}
                      <span className="text-text">À améliorer</span>
                    </li>
                  </ul>
                </div>

                <div className="w-full rounded-xl border border-accent-primary-border/40 bg-accent-primary-subtle p-4 text-left">
                  <div className="flex items-start gap-3">
                    <SparkleIcon className="mt-0.5 size-5 shrink-0 text-icon-brand" weight="bold" />
                    <div>
                      <div className="text-sm font-semibold text-text">Optimisez votre CV</div>
                      <div className="mt-1 text-xs leading-relaxed text-text-secondary">
                        Appliquez nos recommandations pour améliorer votre score.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Panneau Droit : Les 3 cartes */}
              <div className="space-y-4 lg:col-span-8">
                <div>
                  <h4 className="font-medium text-text">Recommandations</h4>
                  <p className="text-xs text-text-muted">
                    Voici les 3 aspects clés à améliorer pour augmenter vos chances de passer les
                    filtres ATS.
                  </p>
                </div>

                {cards.map((card, i) => {
                  const {
                    text: textColor,
                    bg: bgColor,
                    border: borderColor,
                    label: statusLabel,
                  } = getStatusData(card.score);

                  return (
                    <div
                      key={i}
                      className="relative flex flex-col items-start gap-4 rounded-xl border border-border bg-surface p-4 shadow-sm md:flex-row"
                    >
                      <div className="flex flex-1 flex-col gap-1.5">
                        <div className="flex items-center gap-3">
                          <div
                            className={`size-10 rounded-full ${bgColor} flex shrink-0 items-center justify-center`}
                          >
                            <card.icon className={`size-5 ${textColor}`} weight="duotone" />
                          </div>
                          <div>
                            <span className={`text-base font-semibold ${textColor}`}>
                              {card.title}
                            </span>
                          </div>
                          <div
                            className={`ml-auto text-[10px] font-medium ${bgColor} ${textColor} rounded-full border px-2 py-0.5 ${borderColor}`}
                          >
                            {statusLabel}
                          </div>
                        </div>

                        {/* Liste des suggestions */}
                        {card.missingKeywords.length > 0 && (
                          <div className="mt-1 flex items-start gap-2 rounded border border-status-warning-border bg-status-warning-subtle/60 p-2 text-xs text-text-secondary">
                            <SparkleIcon
                              className="mt-0.5 size-3 shrink-0 text-icon-warning"
                              weight="duotone"
                            />
                            <span>
                              <strong className="text-text">Mots-clés manquants :</strong>{" "}
                              {card.missingKeywords.join(", ")}
                            </span>
                          </div>
                        )}

                        {card.points.length > 0 ? (
                          <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-text-secondary marker:text-text-muted">
                            {card.points.map((point: string, idx: number) => (
                              <li key={idx}>{point}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-1 text-xs italic text-text-muted">
                            Aucune suggestion spécifique pour cette catégorie.
                          </p>
                        )}
                      </div>

                      {/* Jauge circulaire de la carte */}
                      {/* <div className="md:self-center shrink-0">
                        <CircularDonut value={card.score} colorClass={textColor} size={60} strokeWidth={4} />
                      </div> */}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 3. ETAT : FORMULAIRE INITIAL */}
          {!score.isPending && !showLastResult && (
            <div className="mx-auto flex max-w-xl flex-col py-4">
              <div className="mb-4 flex flex-col items-center text-center">
                <span className="text-lg font-bold text-text">Calculer votre score ATS</span>
                <span className="text-sm text-text-secondary">
                  Calculez la compatibilité de votre CV avec le système ATS
                </span>
              </div>

              <Form {...formAts}>
                <form onSubmit={formAts.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    name="jobdesc"
                    control={formAts.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-2">
                        <FormLabel className="text-sm font-medium text-text-secondary">
                          Description du poste
                        </FormLabel>
                        <textarea
                          {...field}
                          className="min-h-[120px] w-full resize-y rounded-lg border border-border-input bg-transparent px-4 py-3 text-sm text-text placeholder:text-text-placeholder focus:outline-none focus:ring-1 focus:ring-focus"
                          placeholder="Décrivez le poste ou collez l'offre d'emploi pour obtenir une analyse précise..."
                          required
                        />
                      </FormItem>
                    )}
                  />
                  <div className="mt-6 flex justify-center">
                    <Button
                      type="submit"
                      className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-action-primary font-medium text-action-primary-foreground hover:bg-action-primary-hover"
                    >
                      <ShieldCheckIcon className="size-5" />
                      Calculer mon score ATS
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          )}
        </ScrollArea>

        {/* FOOTER
        {score.isPending && (
          <DialogFooter className="p-4 border-t border-border-subtle bg-surface">
            <Button variant="secondary" onClick={close} className="min-w-[100px]">Annuler</Button>
          </DialogFooter>
        )} */}

        {!score.isPending && value && (
          <DialogFooter className="flex flex-col items-center justify-between gap-3 border-t border-border-subtle bg-surface p-4 md:flex-row">
            {/* <div className="w-full md:w-auto">
              <Button variant="outline" className="w-full md:w-auto flex items-center gap-2 border-border-input hover:bg-surface-hover">
                <DownloadSimpleIcon className="size-4" />
                Télécharger le rapport
              </Button>
            </div> */}
            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
              <Button
                variant="secondary"
                className="flex w-full items-center gap-2 border-border-input md:w-auto"
                onClick={() => {
                  setShowLastResult(false);
                }}
              >
                <ArrowClockwiseIcon className="size-4" />
                Refaire l'analyse
              </Button>
              {!showLastResult && (
                <Button
                  className="flex w-full items-center gap-2 bg-action-primary text-action-primary-foreground hover:bg-action-primary-hover md:w-auto"
                  onClick={() => {
                    setShowLastResult(true);
                  }}
                >
                  <SparkleIcon className="size-4" />
                  Voir la précedente note
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
